'use client';

import classnames from 'classnames';
import {
  MatchStats,
  MatchStatsConfigGroup,
  MatchStatsConfigStatKey,
  MatchStatsProps,
} from '@stadionhq-v3/football-ui';
import { AblyComponent } from '@stadionhq-v3/ably';
import { QA_MATCH_CENTRE_MATCH_STATS } from '@test-config/testIds';
import styles from './matchCentreMatchStats.module.scss';
import { MatchCentreMatchStatsProps } from './matchCentreMatchStats.types';

type ComponentProps = Pick<MatchStatsProps, 'stats'>;

const MatchCentreMatchStats = ({
  /* props */
  testId = QA_MATCH_CENTRE_MATCH_STATS.COMPONENT,
  homeClubCrest,
  awayClubCrest,
  vsLabel,
  matchStatsConfig,
  labels,
  classNames = [],
  nativeProps = {},
  primaryTeam,
  ...ablyProps
}: MatchCentreMatchStatsProps) => {
  const classes = classnames([styles.matchCentreMatchStats], classNames);

  return ablyProps ? (
    <AblyComponent<ComponentProps>
      {...ablyProps}
      storeKey="Match Stats"
      responseKey="stats"
      render={(liveProps: ComponentProps) => {
        const stats = Array.isArray(liveProps?.stats) ? liveProps.stats : [];

        // The possession stat uses a pitch visualisation that only has data once
        // the match is under way (its entry is absent from the feed pre-match).
        // MatchStats renders a group heading for every configured group even when
        // that group has no stats, so leaving `possession` in the config shows a
        // "Possession" title above an empty pitch. Omit the possession group
        // until its data arrives — the other groups still render (with zeroed
        // values) pre-match, and possession appears once the pitch can render.
        const hasPossession = stats.some(
          (stat) => stat.statId === 'possession'
        );
        const effectiveConfig = hasPossession
          ? matchStatsConfig
          : (Object.fromEntries(
              Object.entries(matchStatsConfig).filter(
                ([group]) => group !== 'possession'
              )
            ) as typeof matchStatsConfig);

        return (
          <MatchStats<MatchStatsConfigGroup, MatchStatsConfigStatKey>
            classNames={[classes]}
            testId={testId}
            {...nativeProps}
            {...liveProps}
            homeClubCrest={homeClubCrest}
            awayClubCrest={awayClubCrest}
            vsLabel={vsLabel}
            matchStatsConfig={effectiveConfig}
            labels={labels}
            progressWidth={4}
            primaryTeam={primaryTeam}
          />
        );
      }}
    />
  ) : (
    <></>
  );
};

export default MatchCentreMatchStats;
